import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin, faFacebook, faInstagram, faXTwitter, faYoutube,
  faTiktok, faPinterest, faThreads, faGoogle, faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { WizardModal, WizardStep } from '@/components/ui/WizardModal';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { Toggle } from '@/components/ui/Toggle';
import { NumberStepper } from '@/components/ui/NumberStepper';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useAuth } from '@/hooks/useAuth';
import { useChannels } from '@/hooks/useChannels';
import { Channel } from '@/services/channels.service';
import { isSocialPublishTarget, getPlatformInfo } from '@/constants/platforms';
import { getChannelAvatar } from '@/utils/channel-avatar';
import {
  useWeeklyGoals, useUpsertWeeklyGoals, GoalType,
} from '@/hooks/useDashboardStats';
import { toast } from '@/utils/toast';
import { formatApiError } from '@/services/api';

interface EditGoalsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelId: number | null;
  initialScope?: 'personal' | 'team';
}

// Platform icon mapping
const PLATFORM_FA_ICONS: Record<string, IconDefinition> = {
  LINKEDIN: faLinkedin, FACEBOOK: faFacebook, INSTAGRAM: faInstagram,
  TWITTER: faXTwitter, YOUTUBE: faYoutube, TIKTOK: faTiktok,
  PINTEREST: faPinterest, THREADS: faThreads,
  GOOGLE_MY_BUSINESS: faGoogle, BLUESKY: faBluesky,
};

const PLATFORM_COLORS: Record<string, string> = {
  LINKEDIN: '#0A66C2', FACEBOOK: '#1877F2', INSTAGRAM: '#E4405F',
  TWITTER: '#000000', YOUTUBE: '#FF0000', TIKTOK: '#000000',
  PINTEREST: '#E60023', THREADS: '#000000',
  GOOGLE_MY_BUSINESS: '#4285F4', BLUESKY: '#0085FF',
};

const PLATFORM_LABELS: Record<string, string> = {
  LINKEDIN: 'LinkedIn', FACEBOOK: 'Facebook', INSTAGRAM: 'Instagram',
  TWITTER: 'X (Twitter)', YOUTUBE: 'YouTube', TIKTOK: 'TikTok',
  PINTEREST: 'Pinterest', THREADS: 'Threads',
  GOOGLE_MY_BUSINESS: 'Google Business', BLUESKY: 'Bluesky',
};

// Goal configuration
const GOAL_TYPE_CONFIG: Record<GoalType, {
  labelKey: string;
  defaultLabel: string;
  unit: string;
  suffix: string;
  min: number;
  max: number;
  step: number;
  defaultTarget: number;
}> = {
  POSTS_PUBLISHED: {
    labelKey: 'goal_posts_published',
    defaultLabel: 'Posts Published',
    unit: 'Posts',
    suffix: '',
    min: 1,
    max: 500,
    step: 1,
    defaultTarget: 10,
  },
  ENGAGEMENT_RATE: {
    labelKey: 'goal_engagement_rate',
    defaultLabel: 'Engagement Rate',
    unit: '%',
    suffix: '%',
    min: 0,
    max: 100,
    step: 1,
    defaultTarget: 5,
  },
  TOTAL_REACH: {
    labelKey: 'goal_total_reach',
    defaultLabel: 'Total Reach',
    unit: '',
    suffix: '',
    min: 100,
    max: 10000000,
    step: 100,
    defaultTarget: 5000,
  },
  FOLLOWER_GROWTH: {
    labelKey: 'goal_follower_growth',
    defaultLabel: 'Follower Growth',
    unit: 'Followers',
    suffix: '',
    min: 1,
    max: 1000000,
    step: 1,
    defaultTarget: 100,
  },
};

const GOAL_TYPES: GoalType[] = ['POSTS_PUBLISHED', 'ENGAGEMENT_RATE', 'TOTAL_REACH', 'FOLLOWER_GROWTH'];

const WIZARD_STEPS: WizardStep[] = [
  { id: 'scope', label: 'Scope' },
  { id: 'channel', label: 'Channel' },
  { id: 'goals', label: 'Goals' },
];

const STEP_DESCRIPTIONS = [
  'Choose who this goal is for',
  'Select the scope for your goals',
  'Configure your weekly targets',
];

export const EditGoalsModal = ({ open, onOpenChange, channelId, initialScope }: EditGoalsModalProps) => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const { data: channelsData } = useChannels();
  const { data: allGoals } = useWeeklyGoals();
  const upsertMutation = useUpsertWeeklyGoals();

  // Filter active channels — social publish targets only (excludes CANVA/
  // GDRIVE/WORDPRESS asset channels) plus the separately-discontinued TWITTER.
  const channels = useMemo(() => {
    return channelsData?.filter(
      (c: Channel) => c.isActive && isSocialPublishTarget(c.platform) && c.platform !== 'TWITTER'
    ) || [];
  }, [channelsData]);

  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [scope, setScope] = useState<'personal' | 'team'>(initialScope || 'team');
  const [channelScope, setChannelScope] = useState<'all' | 'platform' | 'channel'>('all');
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(channelId);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [enabledGoals, setEnabledGoals] = useState<Record<GoalType, boolean>>({
    POSTS_PUBLISHED: false,
    ENGAGEMENT_RATE: false,
    TOTAL_REACH: false,
    FOLLOWER_GROWTH: false,
  });
  const [targetValues, setTargetValues] = useState<Record<GoalType, number>>({
    POSTS_PUBLISHED: GOAL_TYPE_CONFIG.POSTS_PUBLISHED.defaultTarget,
    ENGAGEMENT_RATE: GOAL_TYPE_CONFIG.ENGAGEMENT_RATE.defaultTarget,
    TOTAL_REACH: GOAL_TYPE_CONFIG.TOTAL_REACH.defaultTarget,
    FOLLOWER_GROWTH: GOAL_TYPE_CONFIG.FOLLOWER_GROWTH.defaultTarget,
  });

  // Group channels by platform
  const platformGroups = useMemo(() => {
    const groups = new Map<string, Channel[]>();
    channels.forEach((ch) => {
      const list = groups.get(ch.platform) || [];
      list.push(ch);
      groups.set(ch.platform, list);
    });
    return groups;
  }, [channels]);

  // Platform clusters for the Per Channel picker, ordered like the composer's
  // ChannelSelector: most channels first, then alphabetical by platform name.
  const channelClusters = useMemo(
    () =>
      Array.from(platformGroups.entries()).sort(([a, listA], [b, listB]) => {
        const diff = listB.length - listA.length;
        if (diff !== 0) return diff;
        return getPlatformInfo(a).name.localeCompare(getPlatformInfo(b).name);
      }),
    [platformGroups]
  );
  // Explicit user expand/collapse per platform cluster (absent = default)
  const [clusterToggles, setClusterToggles] = useState<Record<string, boolean>>({});

  // Reset on open
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setScope(initialScope || 'team');
      if (channelId) {
        setChannelScope('channel');
        setSelectedChannelId(channelId);
      } else {
        setChannelScope('all');
        setSelectedChannelId(null);
      }
      setSelectedPlatform(null);
      setClusterToggles({});
    }
  }, [open, initialScope, channelId]);

  // Initialize form from existing goals
  useEffect(() => {
    if (!allGoals || !open) return;

    const ownerUserId = scope === 'personal' ? user?.id ?? null : null;
    const filteredGoals = allGoals.filter((g) => {
      if (g.userId !== ownerUserId) return false;
      if (channelScope === 'all') return g.channelId === null && g.platform === null;
      if (channelScope === 'platform') return g.channelId === null && g.platform === selectedPlatform;
      if (channelScope === 'channel') return g.channelId === selectedChannelId;
      return false;
    });

    const newEnabled = {
      POSTS_PUBLISHED: false,
      ENGAGEMENT_RATE: false,
      TOTAL_REACH: false,
      FOLLOWER_GROWTH: false,
    };
    const newTargets = {
      POSTS_PUBLISHED: GOAL_TYPE_CONFIG.POSTS_PUBLISHED.defaultTarget,
      ENGAGEMENT_RATE: GOAL_TYPE_CONFIG.ENGAGEMENT_RATE.defaultTarget,
      TOTAL_REACH: GOAL_TYPE_CONFIG.TOTAL_REACH.defaultTarget,
      FOLLOWER_GROWTH: GOAL_TYPE_CONFIG.FOLLOWER_GROWTH.defaultTarget,
    };

    filteredGoals.forEach((goal) => {
      if (goal.isActive) {
        newEnabled[goal.goalType as GoalType] = true;
        newTargets[goal.goalType as GoalType] = goal.targetValue;
      }
    });

    setEnabledGoals(newEnabled);
    setTargetValues(newTargets);
  }, [allGoals, open, scope, channelScope, selectedChannelId, selectedPlatform, user?.id]);

  // Validation
  const canProceed = useMemo(() => {
    if (currentStep === 0) return true;
    if (currentStep === 1) {
      if (channelScope === 'all') return true;
      if (channelScope === 'platform') return selectedPlatform !== null;
      if (channelScope === 'channel') return selectedChannelId !== null;
    }
    return true;
  }, [currentStep, channelScope, selectedPlatform, selectedChannelId]);

  const hasAnyEnabled = Object.values(enabledGoals).some((v) => v);

  // Handlers
  const handleNext = () => {
    if (canProceed && currentStep < 2) setCurrentStep((s) => s + 1);
  };

  const handleSave = () => {
    const ownerUserId = scope === 'personal' ? user?.id ?? null : null;
    const goalChannelId = channelScope === 'channel' ? selectedChannelId : null;
    const goalPlatform = channelScope === 'platform' ? selectedPlatform : null;

    const activeGoals = GOAL_TYPES
      .filter((type) => enabledGoals[type])
      .map((type) => ({ goalType: type, targetValue: targetValues[type] }));

    upsertMutation.mutate(
      { channelId: goalChannelId, userId: ownerUserId, platform: goalPlatform, goals: activeGoals },
      {
        onSuccess: () => {
          toast.success(t('goals_saved', { defaultValue: 'Goals saved successfully' }));
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(formatApiError(error).message);
        },
      }
    );
  };

  // Footer
  const footer = (
    <div className="flex items-center justify-end gap-1.5 sm:gap-2">
      {currentStep === 0 ? (
        <CancelButton onClick={() => onOpenChange(false)} disabled={upsertMutation.isPending} />
      ) : (
        <Button variant="outline" onClick={() => setCurrentStep((s) => s - 1)} disabled={upsertMutation.isPending}>
          {t('common:back', { defaultValue: 'Back' })}
        </Button>
      )}
      {currentStep < 2 ? (
        <Button variant="primary" onClick={handleNext} disabled={!canProceed}>
          {t('common:next', { defaultValue: 'Next' })}
        </Button>
      ) : (
        <Button variant="primary" onClick={handleSave} loading={upsertMutation.isPending} disabled={!hasAnyEnabled}>
          {t('common:save', { defaultValue: 'Save Goals' })}
        </Button>
      )}
    </div>
  );

  return (
    <WizardModal
      open={open}
      onOpenChange={onOpenChange}
      title={t('dashboard_goals_edit_title', { defaultValue: 'Weekly Goals' })}
      steps={WIZARD_STEPS}
      currentStep={currentStep}
      onStepClick={(i) => { if (i < currentStep) setCurrentStep(i); }}
      stepDescription={STEP_DESCRIPTIONS[currentStep]}
      footer={footer}
      size="lg"
      preventClose={upsertMutation.isPending}
    >
      {/* Step 1: Scope */}
      {currentStep === 0 && (
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setScope('personal')}
            className={`p-6 rounded-xl border-2 text-center transition-all ${
              scope === 'personal'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <FontAwesomeIcon icon={faUser} className={`text-2xl mb-3 ${scope === 'personal' ? 'text-primary-500' : 'text-gray-400'}`} />
            <h4 className="font-semibold text-gray-900">{t('dashboard:scope_personal_title', { defaultValue: 'For Me' })}</h4>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard:scope_personal_desc', { defaultValue: 'Personal weekly goals' })}</p>
          </button>

          <button
            onClick={() => setScope('team')}
            className={`p-6 rounded-xl border-2 text-center transition-all ${
              scope === 'team'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <FontAwesomeIcon icon={faUsers} className={`text-2xl mb-3 ${scope === 'team' ? 'text-primary-500' : 'text-gray-400'}`} />
            <h4 className="font-semibold text-gray-900">{t('dashboard:scope_team_title', { defaultValue: 'For the Team' })}</h4>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard:scope_team_desc', { defaultValue: 'Team-wide weekly goals' })}</p>
          </button>
        </div>
      )}

      {/* Step 2: Channel */}
      {currentStep === 1 && (
        <div>
          <div className="flex gap-2 mb-6">
            {['all', 'platform', 'channel'].map((s) => (
              <button
                key={s}
                onClick={() => {
                  setChannelScope(s as 'all' | 'platform' | 'channel');
                  if (s === 'all') { setSelectedChannelId(null); setSelectedPlatform(null); }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  channelScope === s
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {s === 'all' ? t('dashboard:channel_scope_all', { defaultValue: 'All Channels' }) : s === 'platform' ? t('dashboard:channel_scope_platform', { defaultValue: 'Per Platform' }) : t('dashboard:channel_scope_channel', { defaultValue: 'Per Channel' })}
              </button>
            ))}
          </div>

          {channelScope === 'all' && (
            <div className="flex items-center gap-2 p-4 bg-primary-50 border border-primary-100 rounded-lg">
              <Icon name={ICONS.CHECK} size="sm" color="text-primary-600" />
              <p className="text-sm text-primary-700">{t('dashboard:channel_all_desc', { defaultValue: 'Goals will track across all your connected channels' })}</p>
            </div>
          )}

          {channelScope === 'platform' && (
            <div className="grid grid-cols-3 gap-3">
              {Array.from(platformGroups.entries()).map(([platform, platformChannels]) => (
                <button
                  key={platform}
                  onClick={() => setSelectedPlatform(platform)}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${
                    selectedPlatform === platform
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={PLATFORM_FA_ICONS[platform] || faGlobe}
                    className="text-xl mb-2"
                    style={{ color: PLATFORM_COLORS[platform] || '#6B7280' }}
                  />
                  <p className="text-sm font-medium text-gray-900">{PLATFORM_LABELS[platform] || platform}</p>
                  <p className="text-xs text-gray-500">{platformChannels.length} channel{platformChannels.length !== 1 ? 's' : ''}</p>
                </button>
              ))}
            </div>
          )}

          {channelScope === 'channel' && (
            <div className="picker-scrollbar max-h-[50vh] overflow-y-auto pr-1 space-y-2">
              {channelClusters.map(([platform, clusterChannels], clusterIndex) => {
                const { color, icon, name } = getPlatformInfo(platform);
                const hasSelected = clusterChannels.some((c) => c.id === selectedChannelId);
                // Default: expand the cluster holding the selection, or the first
                // cluster when nothing is selected. Explicit user toggles win.
                const defaultOpen = hasSelected || (selectedChannelId === null && clusterIndex === 0);
                const isOpen = clusterToggles[platform] ?? defaultOpen;
                return (
                  <div key={platform} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => setClusterToggles((prev) => ({ ...prev, [platform]: !isOpen }))}
                      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors"
                    >
                      <span
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: color }}
                      >
                        <FontAwesomeIcon icon={icon} className="text-white" style={{ fontSize: '12px' }} />
                      </span>
                      <span className="text-sm font-medium text-gray-800 truncate">{name}</span>
                      <span className="text-xs text-gray-500">({clusterChannels.length})</span>
                      {hasSelected && (
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary-500" aria-hidden="true" />
                      )}
                      <span
                        className="flex-shrink-0 material-symbols-outlined text-gray-400 text-[18px] ml-auto transition-transform duration-200"
                        style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      >
                        expand_more
                      </span>
                    </button>
                    {isOpen && (
                      <div className="space-y-1.5 p-2 pt-0">
                        {clusterChannels.map((ch) => {
                          const avatarUrl = getChannelAvatar(ch.metadata as Record<string, unknown> | undefined);
                          return (
                            <button
                              key={ch.id}
                              type="button"
                              onClick={() => setSelectedChannelId(ch.id)}
                              className={`w-full flex items-center gap-3 p-2 rounded-lg border-2 transition-all ${
                                selectedChannelId === ch.id
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <div className="relative flex-shrink-0">
                                {avatarUrl ? (
                                  <>
                                    <img src={avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
                                    <span
                                      className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-[1.5px] border-white"
                                      style={{ backgroundColor: color }}
                                    >
                                      <FontAwesomeIcon icon={icon} className="text-white text-[7px]" />
                                    </span>
                                  </>
                                ) : (
                                  <span
                                    className="w-8 h-8 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: color }}
                                  >
                                    <FontAwesomeIcon icon={icon} className="text-white text-sm" />
                                  </span>
                                )}
                              </div>
                              <div className="text-left min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{ch.platformUsername || ch.platform}</p>
                                <p className="text-xs text-gray-500">{PLATFORM_LABELS[ch.platform] || ch.platform}</p>
                              </div>
                              {selectedChannelId === ch.id && (
                                <Icon name={ICONS.CHECK} size="sm" color="text-primary-600" className="ml-auto" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Goals */}
      {currentStep === 2 && (
        <div className="space-y-4">
          {GOAL_TYPES.map((goalType) => {
            const config = GOAL_TYPE_CONFIG[goalType];
            const isEnabled = enabledGoals[goalType];
            const targetValue = targetValues[goalType];

            return (
              <div
                key={goalType}
                className={`flex items-center justify-between gap-4 p-4 rounded-xl border transition-all ${
                  isEnabled ? 'border-primary-200 bg-primary-25' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3 flex-1">
                  <Toggle
                    ariaLabel={t(`dashboard:${config.labelKey}`, { defaultValue: config.defaultLabel })}
                    checked={isEnabled}
                    onChange={() => setEnabledGoals((prev) => ({ ...prev, [goalType]: !prev[goalType] }))}
                  />
                  <span className={`text-sm font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}>
                    {t(`dashboard:${config.labelKey}`, { defaultValue: config.defaultLabel })}
                  </span>
                </div>
                <NumberStepper
                  value={targetValue}
                  onChange={(v) => setTargetValues((prev) => ({ ...prev, [goalType]: v }))}
                  min={config.min}
                  max={config.max}
                  step={config.step}
                  suffix={config.suffix}
                  disabled={!isEnabled}
                />
              </div>
            );
          })}

          {!hasAnyEnabled && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <Icon name={ICONS.ALERT_CIRCLE} size="sm" color="text-amber-500" className="mt-0.5" />
              <p className="text-sm text-amber-700">
                {t('dashboard_goals_enable_at_least_one', {
                  defaultValue: 'Enable at least one goal to track your weekly progress',
                })}
              </p>
            </div>
          )}
        </div>
      )}
    </WizardModal>
  );
};
